import React from 'react';
import PropTypes from 'prop-types';
import ReactRouterPropTypes from 'react-router-prop-types';
import { graphql } from '@apollo/client/react/hoc';
import { compose } from 'ramda';
import { compressToBase64 } from 'lz-string';

import { AppSyncClientCreate } from '../../../backends/AppSyncClientCreate';
import GetMyEndpoint from '../../../q/create/GetMyEndpoint'; // TODO(clairbee): add to 'refetch queries' on update endpoint?
import AddEndpoint from '../../../q/create/AddEndpoint';
import UpdateEndpoint from '../../../q/create/UpdateEndpoint';
import GetMyApp from '../../../q/create/GetMyApp';
import GetMyApps from '../../../q/create/GetMyApps';
import ChangeAppLocation from '../../../q/create/ChangeAppLocation';
import AddEndpointImage from '../../../q/create/AddEndpointImage';
import AddEndpointTrack from '../../../q/create/AddEndpointTrack';
import Endpoint from '../../../modules/data/Endpoint';
import App from '../../../modules/data/App';
import PublisherEndpointForm from '../../../components/forms/app/Endpoint';
import AORTypes from '../../../types';

class PublisherEndpointAdd extends React.Component {
  constructor(props, context) {
    super(props, context);

    // eslint-disable-next-line react/no-unused-class-component-methods
    this.displayName = 'PublisherEndpointAdd';

    this.onCancel = this.onCancel.bind(this);
    this.onNext = this.onNext.bind(this);
    this.onAdd = this.onAdd.bind(this);

    const { app, match: { params: { appUuid } } } = this.props;
    const endpoint = new Endpoint(appUuid);

    app.postProcess();

    // see if there are already endpoints.
    // if yes, initialize the location of the new endpoint using the location of the last one.
    if (app.data.endpoints && app.pp.endpointIds && app.pp.endpointIds.length > 0) {
      const lastId = app.pp.endpointIds[app.pp.endpointIds.length - 1];

      app.data.endpoints.forEach((w) => {
        if (w.uuid === lastId) {
          endpoint.data.lat = w.lat;
          endpoint.data.lon = w.lon;
        }
      });
    }

    this.state = {
      endpoint,
    };
  }

  onCancel() {
    const { history, appUuid } = this.props;
    history.push(`/editapp/${appUuid}`);
  }

  onNext(uuid) {
    const { history, appUuid } = this.props;
    history.push(`/editendpoint/${appUuid}/${uuid}`);
  }

  async onAdd(values) {
    const {
      app, onAdd, onSave, appUpdate, position, getDirectionsService,
      getAnalyticsService,
    } = this.props;
    const { endpoint } = this.state;

    app.postProcess();

    endpoint.data.name = values.name;
    endpoint.data.extraStay = values.extraStay;
    endpoint.data.size = values.size;
    endpoint.data.lat = values.lat ? values.lat : position.latitude;
    endpoint.data.lon = values.lon ? values.lon : position.longitude;
    const out = endpoint.output();

    let func = onAdd;
    if (out.uuid !== undefined && out.uuid !== 1) {
      func = onSave;
    }

    let bounds;
    let route;
    let legs;
    let routeduration;
    let routelen;
    if (app.pp.endpoints && app.pp.endpoints.length > 0 && app.pp.travelType !== 3) {
      // if there was at least one endpoint before, then there will be at least two endpoints now and, thus, a route
      app.pp.endpoints.push(out);
      app.pp.endpointIds.push(out.uuid);
      const routeInfo = await getDirectionsService().getDirections(app);
      bounds = routeInfo.bounds;
      route = routeInfo.route;
      legs = routeInfo.legs;
      routeduration = routeInfo.routeduration;
      routelen = routeInfo.routelen;
    } else {
      bounds = {
        latAvg: endpoint.data.lat,
        lonAvg: endpoint.data.lon,
        latDelta: App.DEFAULT_LAT_DELTA,
        lonDelta: App.DEFAULT_LON_DELTA,
      };
      route = [];
      legs = [];
      routeduration = 0;
      routelen = 0;
    }
    app.pp.bounds = bounds;
    app.pp.route = route;
    app.pp.legs = legs;
    app.pp.routeduration = routeduration;
    app.pp.routelen = routelen;
    app.data.bounds = JSON.stringify(bounds);
    app.data.route = compressToBase64(JSON.stringify(route));
    app.data.legs = compressToBase64(JSON.stringify(legs));
    app.data.routeduration = routeduration;
    app.data.routelen = routelen;
    out.bounds = app.data.bounds;
    out.route = app.data.route;
    out.legs = app.data.legs;
    out.routeduration = routeduration;
    out.routelen = routelen;

    return func(out)
      .then((result) => {
        if (result.data.createEndpoint !== undefined) {
          const { uuid } = result.data.createEndpoint;
          if (uuid !== undefined) {
            endpoint.data.uuid = uuid;
            getAnalyticsService().report.event('endpointAddSuccess', 'create');
          }
        }
        if (app.data.lat !== undefined || app.data.lon !== undefined || app.data.coords !== undefined) {
          // Update app coordinates if neccessary
          // TODO(clairbee): make sure it is enforced on the server side
          app.data.lat = out.lat;
          app.data.lon = out.lon;
          app.data.coords = out.coords;
          return appUpdate(app).then(() => result);
        }
        return result;
      });
  }

  render() {
    const {
      history, direction, position,
      onAddImage,
      onAddTrack,
      getUploader,
      user,
      app,
      getDirectionsService,
      getAnalyticsService,
    } = this.props;
    const { endpoint } = this.state;

    if (!user || !app || !endpoint) {
      return (
        <>
        </>
      );
    }

    return (
      <PublisherEndpointForm
        position={position}
        app={app}
        endpoint={endpoint}
        onCancel={this.onCancel}
        onNext={this.onNext}
        onSave={this.onAdd}
        onAddImage={onAddImage}
        onAddTrack={onAddTrack}
        getUploader={getUploader}
        user={user}
        getDirectionsService={getDirectionsService}
        getAnalyticsService={getAnalyticsService}
        history={history}
        direction={direction}
      />
    );
  }
}

PublisherEndpointAdd.defaultProps = {
  // TODO(clairbee): Even though 'app' is always supplied, we need a temporary value before graphql finishes loading
  app: new App(),
  position: null, // it's unset until it's autodetected
  user: null,
};

PublisherEndpointAdd.propTypes = {
  appUuid: PropTypes.string.isRequired,
  app: PropTypes.instanceOf(App),
  onAdd: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onAddImage: PropTypes.func.isRequired,
  onAddTrack: PropTypes.func.isRequired,
  appUpdate: PropTypes.func.isRequired,
  getUploader: PropTypes.func.isRequired,
  match: ReactRouterPropTypes.match.isRequired,
  position: AORTypes.Position,
  user: AORTypes.AWS.Cognito.User,
  getDirectionsService: PropTypes.func.isRequired,
  getAnalyticsService: PropTypes.func.isRequired,
  history: ReactRouterPropTypes.history.isRequired,
  direction: PropTypes.string.isRequired,
};

export default compose(
  graphql(GetMyApp, {
    // props: props => ({
    //   app: props.data.getMyApps,
    // }),
    props: (props) => {
      // console.log('graphql props');
      // console.log(props);
      if (!props.data.getMyApps) {
        return {};
      }
      const app = App.createFromData(props.data.getMyApps);
      if (app && app.data && app.data.endpoints === undefined) {
        app.data.endpoints = [];
      }
      return {
        onRefresh: props.data.refetch,
        app,
      };
    },
    options: (props) => ({
      variables: {
        uuid: props.match.params.appUuid,
      },
      client: AppSyncClientCreate(props.user),
      fetchPolicy: 'cache-and-network',
    }),
    // client: AppSyncClientCreate,
  }),
  graphql(ChangeAppLocation, {
    props: ({ mutate }) => ({
      appUpdate: (app) => mutate({
        variables: {
          uuid: app.data.uuid,
          travelType: app.data.travelType,
          lat: app.data.lat,
          lon: app.data.lon,
          coords: app.data.coords,
        },
      }),
    }),
    options: (props) => ({
      refetchQueries: [{
        query: GetMyApp,
        variables: { uuid: props.match.params.appUuid },
        client: AppSyncClientCreate(props.user),
      }, {
        query: GetMyApps,
        client: AppSyncClientCreate(props.user),
      }],
      // fetchPolicy: 'cache-and-network',
      client: AppSyncClientCreate(props.user),
    }),
  }),
  graphql(AddEndpoint, {
    props: (props) => ({
      onAdd: (endpoint) => props.mutate({
        variables: endpoint,
        // TODO(clairbee): optimisticResponse breaks refetchQueries
        // optimisticResponse: () => ({
        //   createEndpoint:
        //     endpoint.makeOptimistic().output(),
        // }),
      }),
    }),
    options: (props) => ({
      refetchQueries: [{
        query: GetMyApp,
        variables: { uuid: props.match.params.appUuid },
        client: AppSyncClientCreate(props.user),
      }, {
        query: GetMyApps,
        client: AppSyncClientCreate(props.user),
      }],
      // fetchPolicy: 'cache-and-network',
      // fetchPolicy: 'network-only',
      client: AppSyncClientCreate(props.user),
    }),
  }),
  graphql(UpdateEndpoint, {
    props: (props) => ({
      onSave: (endpoint) => props.mutate({
        variables: endpoint,
        // TODO(clairbee): optimisticResponse breaks refetchQueries
        // optimisticResponse: () => ({
        //   updateEndpoint:
        //     endpoint.makeOptimistic().output(),
        // }),
      }),
    }),
    options: (props) => ({
      refetchQueries: [{
        query: GetMyApp,
        variables: { uuid: props.match.params.appUuid },
        client: AppSyncClientCreate(props.user),
      }, {
        query: GetMyApps,
        client: AppSyncClientCreate(props.user),
      }],
      /* fetchPolicy: 'cache-and-network' */
      client: AppSyncClientCreate(props.user),
    }),
  }),
  graphql(AddEndpointImage, {
    // name: 'AddEndpointImage',
    props: (props) => ({
      onAddImage: ({
        uuid, name, isVertical, lat, lon, type,
      }) => props.mutate({
        variables: {
          uuid,
          name,
          isVertical,
          lat,
          lon,
          type,
        },
        // TODO(clairbee): optimisticResponse breaks refetchQueries
        // optimisticResponse: () => ({
        //   updateEndpoint:
        //     endpoint.makeOptimistic().output(),
        // }),
      }),
    }),
    options: (props) => ({
      refetchQueries: [{
        query: GetMyApp,
        variables: { uuid: props.match.params.appUuid },
        client: AppSyncClientCreate(props.user),
      }, {
        query: GetMyApps,
        client: AppSyncClientCreate(props.user),
      }, {
        query: GetMyEndpoint,
        props: (p) => ({
          endpoint: Endpoint.createFromData(p.data.getMyEndpoint),
        }),
        variables: { uuid: props.uuid },
        // variables: { uuid: props.endpoint.data.uuid },
        client: AppSyncClientCreate(props.user),
      }],
      /* fetchPolicy: 'cache-and-network' */
      // fetchPolicy: 'no-cache',
      // fetchPolicy: 'network-only',
      client: AppSyncClientCreate(props.user),
    }),
  }),
  graphql(AddEndpointTrack, {
    // name: 'AddEndpointTrack',
    props: (props) => ({
      onAddTrack: ({
        uuid, name, type,
      }) => props.mutate({
        variables: {
          uuid,
          name,
          type,
        },
        // TODO(clairbee): optimisticResponse breaks refetchQueries
        // optimisticResponse: () => ({
        //   updateEndpoint:
        //     endpoint.makeOptimistic().output(),
        // }),
      }),
    }),
    options: (props) => ({
      refetchQueries: [{
        query: GetMyApp,
        variables: { uuid: props.match.params.appUuid },
        client: AppSyncClientCreate(props.user),
      }, {
        query: GetMyApps,
        client: AppSyncClientCreate(props.user),
      }, {
        query: GetMyEndpoint,
        props: (p) => ({
          endpoint: Endpoint.createFromData(p.data.getMyEndpoint),
        }),
        variables: { uuid: props.uuid },
        // variables: { uuid: props.endpoint.data.uuid },
        client: AppSyncClientCreate(props.user),
      }],
      /* fetchPolicy: 'cache-and-network' */
      // fetchPolicy: 'no-cache',
      // fetchPolicy: 'network-only',
      client: AppSyncClientCreate(props.user),
    }),
  })
)(PublisherEndpointAdd);
